import type {
  BoletoBankStatus,
  BoletoProvider,
  BoletoStatus,
  CnabReturnItemStatus,
  ContactStatement,
  StatementLineStatus,
  StatementLineView,
  StatementMatchKind,
} from "@cx/contracts";
import { formatBRL, formatDigitableLine } from "@cx/domain";
import type { Tone } from "@cx/ui";

// ===== Boleto, CNAB, conciliação do extrato e ficha financeira (T2.10) =====

export const BOLETO_PROVIDER_LABEL: Record<BoletoProvider, string> = {
  asaas: "Asaas",
  sandbox: "Sandbox (simulado, para testes)",
  bank_cnab: "Carteira própria (remessa CNAB)",
};

export const BOLETO_BADGE: Record<BoletoStatus, { label: string; tone: Tone }> = {
  creating: { label: "Gerando", tone: "secondary" },
  active: { label: "Aguardando pagamento", tone: "info" },
  expired: { label: "Vencido", tone: "warning" },
  paid: { label: "Pago", tone: "success" },
  canceled: { label: "Cancelado", tone: "secondary" },
  failed: { label: "Falhou", tone: "danger" },
};

/** Registro no banco (carteira própria). */
export const BANK_STATUS_LABEL: Record<NonNullable<BoletoBankStatus>, string> = {
  pending: "Aguardando remessa",
  sent: "Enviado na remessa",
  registered: "Registrado no banco",
  rejected: "Recusado pelo banco",
  cancel_requested: "Baixa pedida (vai na próxima remessa)",
  cancel_sent: "Baixa enviada ao banco",
};

/** Linha digitável com os pontos e espaços de sempre; texto fora do formato fica como veio. */
export function digitableLineLabel(line: string | null): string {
  if (!line) return "—";
  try {
    return formatDigitableLine(line);
  } catch {
    return line;
  }
}

export const RETURN_ITEM_BADGE: Record<CnabReturnItemStatus, { label: string; tone: Tone }> = {
  applied: { label: "Processada", tone: "success" },
  duplicate: { label: "Repetida (ignorada)", tone: "secondary" },
  review: { label: "Revisar", tone: "warning" },
  info: { label: "Informativa", tone: "info" },
  ignored: { label: "Ignorada", tone: "secondary" },
};

/** Resumo do retorno importado para o aviso. */
export function returnSummary(f: { applied: number; duplicates: number; review: number }): string {
  const parts = [`${f.applied} ${f.applied === 1 ? "ocorrência processada" : "ocorrências processadas"}`];
  if (f.duplicates) parts.push(`${f.duplicates} ${f.duplicates === 1 ? "repetida" : "repetidas"}`);
  if (f.review) parts.push(`${f.review} para revisar`);
  return parts.join(", ");
}

// ---------- conciliação do extrato ----------

export const LINE_FILTERS: { value: StatementLineStatus | "pending" | "all"; label: string }[] = [
  { value: "pending", label: "Pendentes" },
  { value: "suggested", label: "Sugeridos pela IA" },
  { value: "matched", label: "Conciliados" },
  { value: "ignored", label: "Ignorados" },
  { value: "all", label: "Todos" },
];

export const LINE_BADGE: Record<StatementLineStatus, { label: string; tone: Tone }> = {
  unmatched: { label: "Pendente", tone: "warning" },
  suggested: { label: "Sugestão da IA", tone: "info" },
  matched: { label: "Conciliado", tone: "success" },
  ignored: { label: "Ignorado", tone: "secondary" },
};

export const MATCH_KIND_LABEL: Record<StatementMatchKind, string> = {
  titles: "baixa de contas",
  transaction: "lançamento existente",
  create: "lançamento novo",
  ignore: "ignorado",
};

/** Histórico do banco: nome e memo sem repetição. */
export function lineText(l: Pick<StatementLineView, "name" | "memo" | "type">): string {
  const parts = [l.name, l.memo].filter((s): s is string => !!s && !!s.trim());
  const uniq = parts.filter((s, i) => parts.findIndex((x) => x.trim() === s.trim()) === i);
  return uniq.join(" · ") || l.type;
}

/** Confiança 0–1 000 000 (pct4) em "87%". */
export function confidenceLabel(pct4: number | null): string | null {
  if (pct4 === null) return null;
  return `${Math.round(pct4 / 10_000)}%`;
}

// ---------- ficha financeira ----------

export const STATEMENT_FILTERS: { value: "all" | "open" | "overdue" | "paid"; label: string }[] = [
  { value: "all", label: "Todas" },
  { value: "open", label: "Em aberto" },
  { value: "overdue", label: "Atrasadas" },
  { value: "paid", label: "Quitadas" },
];

/** Limite de crédito do cliente (RF-CAD-01) em texto. */
export function creditLabel(c: ContactStatement["credit"]): string | null {
  if (!c) return null;
  if (c.mode === "unlimited") return "Sem limite de crédito";
  if (c.mode === "zero") return "Só à vista (limite zero)";
  if (c.limitCents === null) return null;
  const avail = c.availableCents === null ? "" : ` · disponível ${formatBRL(c.availableCents)}`;
  return `Limite ${formatBRL(c.limitCents)}${avail}${c.exceeded ? " · limite excedido" : ""}`;
}
